<script setup lang="ts">
/**
 * @name LoadingState
 * @version 1.0.0
 * @description Componente reutilizable para mostrar estados de carga
 * @author @Jorel254
 * @changelog 1.0.0 - 2025-01-17 - @Jorel254 - Versión inicial
 */

interface Props {
	loadingProgress?: number
	showProgress?: boolean
	title?: string
	description?: string
}

const props = withDefaults(defineProps<Props>(), {
	loadingProgress: 0,
	showProgress: false,
	title: 'Cargando...',
	description: 'Preparando la vista optimizada para mejor rendimiento'
})
</script>

<template>
	<div class="loading-state">
		<div class="loader">
			<div class="loader-spinner"></div>
			<div class="loader-dots">
				<span></span>
				<span></span>
				<span></span>
			</div>
		</div>
		<h3 class="loading-title">{{ props.title }}</h3>
		<p class="loading-description">{{ props.description }}</p>

		<!-- Progress Bar -->
		<div v-if="props.showProgress" class="progress-container">
			<div class="progress-bar">
				<div class="progress-fill" :style="{ width: `${props.loadingProgress}%` }"></div>
			</div>
			<p class="progress-text">{{ props.loadingProgress }}% completado</p>
		</div>
	</div>
</template>

<style scoped>
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	min-height: 400px;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 12px;
	margin: 1rem;
}

.loader {
	position: relative;
	margin-bottom: 2rem;
}

.loader-spinner {
	border: 6px solid #e9ecef;
	border-radius: 50%;
	border-top: 6px solid #007bff;
	width: 80px;
	height: 80px;
	animation: spin 1.2s linear infinite;
	box-shadow: 0 8px 24px rgba(0, 123, 255, 0.15);
}

.loader-dots {
	display: flex;
	justify-content: center;
	gap: 8px;
	margin-top: 1rem;
}

.loader-dots span {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: #007bff;
	animation: dots 1.4s ease-in-out infinite both;
}

.loader-dots span:nth-child(1) {
	animation-delay: -0.32s;
}

.loader-dots span:nth-child(2) {
	animation-delay: -0.16s;
}

.loading-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 0.75rem;
}

.loading-description {
	color: #6c757d;
	font-size: 1.1rem;
	max-width: 400px;
	line-height: 1.5;
}

/* Progress Bar */
.progress-container {
	margin-top: 2rem;
	width: 100%;
	max-width: 300px;
}

.progress-bar {
	width: 100%;
	height: 8px;
	background: #e9ecef;
	border-radius: 4px;
	overflow: hidden;
	margin-bottom: 0.5rem;
}

.progress-fill {
	height: 100%;
	background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
	border-radius: 4px;
	transition: width 0.3s ease;
}

.progress-text {
	color: #6c757d;
	font-size: 0.9rem;
	margin: 0;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes dots {
	0%,
	80%,
	100% {
		transform: scale(0);
		opacity: 0.5;
	}
	40% {
		transform: scale(1);
		opacity: 1;
	}
}
</style>
